/**
 * Deep Research Annotation Component
 *
 * Displays task tracking and progress for deep research tool execution
 *
 * Primary responsibilities:
 * - Show live task progress during research
 * - Display completed research tasks with results
 * - Integrate ResearchTaskTracker component
 * - Handle research tool annotations
 *
 * Dependencies:
 * - ResearchTaskTracker component
 * - useResearchTasks hook
 */

'use client'

import React, { useCallback, useEffect, useState } from 'react'
import {
  Search,
  Brain,
  FileText,
  CheckCircle,
  Clock,
  Download,
  ChevronDown,
  ChevronUp,
  PanelRightOpen
} from 'lucide-react'
import { Streamdown } from 'streamdown'
import { ResearchTaskTracker } from '@/components/ui/research-task-tracker'
import { useResearchTasks } from '@/hooks/useResearchTasks'
import { Annotation, Annotation2 } from '@/types/annotation'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { stripMarkdownForPreview } from '@/lib/strip-markdown-for-preview'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'

// --------------

/** Extract first H1 from markdown and return a safe filename (no extension). */
function filenameFromReportH1(report: string): string {
  const match = report.match(/^#\s+(.+)$/m)
  const title = match ? match[1].trim() : 'Deep Research Report'
  const sanitized = title
    .replace(/[^\p{L}\p{N}\s\-_.]/gu, ' ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return sanitized.slice(0, 80) || 'Deep-Research-Report'
}

function downloadReportAsMd(report: string, filenameBase: string) {
  const filename = `${filenameBase}.md`
  const blob = new Blob([report], { type: 'text/markdown;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface DeepResearchAnnotationProps {
  annotation: Annotation | Annotation2
}

// --------------

export const DeepResearchAnnotation: React.FC<DeepResearchAnnotationProps> = ({ annotation }) => {
  const { tasks, isResearching } = useResearchTasks()
  const [showTracker, setShowTracker] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  // Check if this annotation contains deep research data (any key: deepResearch, platformTools, or deepResearch__*)
  const ann = annotation as Record<string, unknown>
  const deepResearchData =
    ann?.deepResearch ??
    ann?.platformTools ??
    Object.entries(ann).find(([k]) => k.startsWith('deepResearch') || k === 'platformTools')?.[1]

  // Shape of deep research tool result (metadata, report, or result)
  type DeepResearchMetadata = {
    topic?: string
    entityType?: string
    confidenceScore?: number | string
    totalSources?: number
    phasesCompleted?: number
    agentIntelligence?: {
      classificationConfidence?: number | string
      agentConsensus?: boolean
      strategicDimensions?: string | number
      rejectedDimensions?: string | number
    }
    [key: string]: unknown
  }
  type DeepResearchResultShape = {
    metadata?: DeepResearchMetadata
    report?: string
    result?: string
  }

  // Extract the actual result if it's wrapped in a ToolResult structure
  const rawResult =
    deepResearchData && typeof deepResearchData === 'object' && 'result' in deepResearchData
      ? (deepResearchData as { result?: unknown }).result
      : deepResearchData
  const researchResult = rawResult as DeepResearchResultShape | null | undefined

  // Get research metadata and report text (computed unconditionally for hook deps)
  const metadata: DeepResearchMetadata = researchResult?.metadata ?? {}
  const reportText =
    typeof researchResult?.report === 'string'
      ? researchResult.report
      : typeof researchResult?.result === 'string'
        ? researchResult.result
        : null
  const hasResults = !!reportText || !!(researchResult?.report ?? researchResult?.result)

  // Hooks must run unconditionally (before any early return)
  const handleDownloadMd = useCallback(() => {
    if (!reportText) return
    const filenameBase = filenameFromReportH1(reportText)
    downloadReportAsMd(reportText, filenameBase)
  }, [reportText])

  const handleOpenInPanel = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('openDeepResearchPanel', {
        detail: { report: reportText ?? undefined, metadata }
      })
    )
  }, [reportText, metadata])

  useEffect(() => {
    if (researchResult || isResearching || tasks.length > 0) {
      setShowTracker(true)
    }
  }, [researchResult, isResearching, tasks.length])

  // Don't render if no deep research data and no active research
  if (!showTracker) {
    return null
  }

  const topicDisplay: string | null =
    metadata.topic && typeof metadata.topic === 'string'
      ? (() => {
          const plain = stripMarkdownForPreview(metadata.topic)
          return plain.length > 100 ? `${plain.slice(0, 100).trim()}…` : plain
        })()
      : null

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} className="w-full">
      {/* Compact header: one row when collapsed, clear expand affordance */}
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border/70 bg-card px-4 py-3 text-left outline-none hover:border-border hover:bg-muted/25 focus-visible:ring-2 focus-visible:ring-primary/20 motion-safe:transition-[box-shadow,background-color,border-color]"
          aria-expanded={isExpanded}
        >
          <div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground">
            {isResearching ? (
              <Brain className="size-4 text-primary motion-safe:animate-pulse motion-reduce:animate-none" />
            ) : hasResults ? (
              <CheckCircle className="size-4 text-emerald-500" />
            ) : (
              <Search className="size-4 text-primary" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">
              {isResearching ? 'Agent workforce research' : 'Deep research analysis'}
            </div>
            {topicDisplay && (
              <div className="mt-0.5 truncate text-xs text-muted-foreground">{topicDisplay}</div>
            )}
            {hasResults && metadata.entityType && !isExpanded && (
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {String(metadata.entityType)}
                {metadata.confidenceScore != null && (
                  <span className="ml-1 opacity-80">
                    · {Math.round(Number(metadata.confidenceScore) * 100)}% confidence
                  </span>
                )}
              </div>
            )}
          </div>

          {hasResults && (
            <div className="flex shrink-0 items-center gap-2">
              {metadata.totalSources != null && (
                <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <FileText className="size-3.5" />
                  {String(metadata.totalSources)}
                </span>
              )}
              {metadata.phasesCompleted != null && (
                <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  <Clock className="size-3.5" />
                  {String(metadata.phasesCompleted)} phases
                </span>
              )}
            </div>
          )}

          <div
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground group-hover:bg-muted group-hover:text-foreground motion-safe:transition-colors"
            aria-hidden
          >
            {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-4 rounded-b-lg border-x border-b border-border/70 bg-card px-4 pb-4 pt-3">
          {hasResults && metadata.entityType && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Classified as</span>{' '}
              {String(metadata.entityType)}
              {metadata.confidenceScore != null && (
                <span className="ml-2 text-muted-foreground">
                  {Math.round(Number(metadata.confidenceScore) * 100)}% confidence
                </span>
              )}
            </div>
          )}

          {(hasResults && reportText) || isResearching || tasks.length > 0 ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.preventDefault()
                  handleOpenInPanel()
                }}
              >
                <PanelRightOpen className="size-4" />
                Open in panel
              </Button>
              {hasResults && reportText && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.preventDefault()
                    handleDownloadMd()
                  }}
                >
                  <Download className="size-4" />
                  Download .md
                </Button>
              )}
            </div>
          ) : null}

          <ResearchTaskTracker tasks={tasks} className="min-h-[160px]" />

          {reportText && (
            <div className="rounded-lg border border-border/70 bg-muted/20 p-4">
              <div className="prose prose-neutral max-w-none text-sm dark:prose-invert prose-headings:scroll-mt-4 prose-headings:font-semibold prose-p:leading-relaxed prose-a:text-primary prose-pre:bg-muted prose-pre:text-xs prose-pre:text-foreground">
                <Streamdown {...streamdownStaticProps}>{reportText}</Streamdown>
              </div>
            </div>
          )}

          {hasResults && metadata.agentIntelligence && (
            <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 p-4 dark:border-emerald-800/50 dark:bg-emerald-900/15">
              <div className="mb-3 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                Agent intelligence summary
              </div>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div className="flex justify-between gap-2 sm:block">
                  <span className="text-emerald-700 dark:text-emerald-400">Classification</span>
                  <span className="font-medium text-emerald-900 dark:text-emerald-100">
                    {metadata.agentIntelligence?.classificationConfidence != null
                      ? String(metadata.agentIntelligence.classificationConfidence)
                      : '—'}
                    /10
                  </span>
                </div>
                <div className="flex justify-between gap-2 sm:block">
                  <span className="text-emerald-700 dark:text-emerald-400">Consensus</span>
                  <span className="font-medium text-emerald-900 dark:text-emerald-100">
                    {metadata.agentIntelligence?.agentConsensus ? 'Yes' : 'Conflicts'}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-emerald-700 dark:text-emerald-400">Dimensions</span>
                  <span className="ml-2 font-medium text-emerald-900 dark:text-emerald-100">
                    {metadata.agentIntelligence?.strategicDimensions != null
                      ? String(metadata.agentIntelligence.strategicDimensions)
                      : '—'}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <span className="text-emerald-700 dark:text-emerald-400">Rejected</span>
                  <span className="ml-2 font-medium text-emerald-900 dark:text-emerald-100">
                    {metadata.agentIntelligence?.rejectedDimensions != null
                      ? String(metadata.agentIntelligence.rejectedDimensions)
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export default DeepResearchAnnotation
